import { Shape, ShapeType } from '@/services/shape';
import generateID from '../generate-id';
import { ResourceKey } from '@/services/shape/attributes';
import { cloneDeep, round } from 'lodash-es';
import KonvaService from '@/services/konva';
import { useEditorStore } from '@/store/use-editor-store';
import { useImagesStore } from '@/store/use-images-store';
import { useRuntimeStore } from '@/store/use-runtime-store';
import { KonvaUtils } from '@/services/konva/konva.utils';
import { dataSourceWholeItems } from '@/resources/data-source';
import { DataSourceFormat } from '@/resources/enum';
import dayjs from 'dayjs';
import {
  canUse,
  canUseDataSource,
  disabledDataSourceFormat
} from '../can-use.utils';
import {
  getUnSupportShapesByWatchfaceType,
  useTextFont,
  WatchfaceDeviceType,
  useDataSource
} from '@/hooks';
import { TextString } from '@/services/shape/attributes/text';
import { resetResources } from './pre-handle';
import { Description } from '@/types/watchface';
import {
  supportFahrenheit,
  supportTemperatureType
} from '../ast/utils/image-array.utils';

interface LayersRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** 图组数字填充：根据 sourceItems 计算位数基准（用于包围盒宽度），含 returnValue 如 [00:00, 24:00) 时减 1 因冒号为图片占位 */
function getImageArrayDigitCountBase(
  sourceItems: Array<
    { maxValue?: number; minValue?: number; returnValue?: string } | undefined
  >
): number {
  const items = sourceItems.filter(Boolean) as Array<{
    maxValue?: number;
    minValue?: number;
    returnValue?: string;
  }>;
  const allValues = items.flatMap((item) => [
    item?.maxValue ?? 0,
    item?.minValue ?? 0
  ]);
  const allLengths = allValues.map((value) => value.toString().length);
  const returnValue = items[0]?.returnValue?.match(/\[(\d{2}:\d{2})\s*,/)?.[1];
  const returnValueLength = returnValue ? returnValue.toString().length - 1 : 0;
  return (returnValueLength || Math.max(...allLengths, 1)) ?? 4;
}

export class LayerUtils {
  static calcLayersRect(layers: StoreLayerItem[], isGroup = false): LayersRect {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const layer of layers) {
      const { id } = layer;
      const shape = Shape.getShape(id);
      if (!shape) continue;
      const konvaService = KonvaService.getIns();
      let {
        x = 0,
        y = 0,
        width = 0,
        height = 0
      } = konvaService?.getClientRect(id, isGroup) ?? {};
      // 图组数字填充且有单位图时：组宽=图组w+单位w（+间距），组高=max(图组h,单位h)，并考虑图组 scale
      // widgetImageArray 的宽度已在 getClientRect 中精确计算，此处跳过避免双重修正
      if (
        shape.type === ShapeType.ImageArray &&
        shape.attrs.fillType === '数字填充' &&
        shape.attrs.resources?.unitIcon &&
        !shape.widgetImageArray
      ) {
        const imageStore = useImagesStore();
        const colorId =
          useEditorStore().themeColor?.id ??
          Object.keys(shape.attrs.resources?.unitIcon ?? {})[0] ??
          Object.keys(shape.attrs.resources?.images ?? {})[0];
        if (colorId) {
          let unitW = shape.getImageSize('unitIcon', colorId)?.width ?? 0;
          let unitH = shape.getImageSize('unitIcon', colorId)?.height ?? 0;
          if (!unitW && !unitH) {
            const res = shape.attrs.resources.unitIcon as Record<
              string,
              string[]
            >;
            Object.values(res || {}).forEach((srcs) => {
              const arr = Array.isArray(srcs) ? srcs : [srcs];
              arr.forEach((src) => {
                if (!src) return;
                const item =
                  imageStore.imagesMap[src] ??
                  imageStore.imagesMap[
                    /[/\\]/.test(src) ? src.replace(/^.*[/\\]/, '') : src
                  ];
                if (item?.width != null)
                  unitW = Math.max(unitW, item.width ?? 0);
                if (item?.height != null)
                  unitH = Math.max(unitH, item.height ?? 0);
              });
            });
          }
          if (unitW || unitH) {
            const {
              w: cw = 0,
              h: ch = 0,
              space = 0,
              decimalOffsetX = 0,
              scale: scaleAttr = 1
            } = shape.attrs;
            const scale =
              scaleAttr >= 10
                ? Number(scaleAttr) / 100
                : Number(scaleAttr) || 1;
            const sourceItems = shape.sourceItems ?? [];
            const includePrecision = sourceItems.some((i) => !!i?.precision);
            let digitCount = getImageArrayDigitCountBase(sourceItems);
            if (includePrecision) {
              let _p = 0;
              sourceItems.forEach((i) => {
                if (i?.precision && i.precision > _p) _p = i.precision;
              });
              digitCount += _p + 1;
            }
            const digitPart =
              digitCount * cw +
              (digitCount - 1) * space +
              Number(decimalOffsetX ?? 0);
            // 组宽=图组w+单位w（+间距），缩放后
            const groupW = (digitPart + space + unitW) * scale;
            const groupH = Math.max(ch, unitH) * scale;
            width = Math.max(width, Math.ceil(groupW));
            height = Math.max(height, Math.ceil(groupH));
          }
        }
      }

      // 指针图片（绑定了数据源的图片）：以 pivot 为圆心，pivot 到四角最远距离为半径，计算旋转扫过的正方形区域
      if (shape.isPointerImage) {
        const { attrs } = shape;
        const w0 = attrs.w ?? width;
        const h0 = attrs.h ?? height;
        const px = attrs.pivotX !== undefined ? Number(attrs.pivotX) : w0 / 2;
        const py = attrs.pivotY !== undefined ? Number(attrs.pivotY) : h0 / 2;
        const radius = Math.ceil(Math.max(
          Math.hypot(px, py),
          Math.hypot(w0 - px, py),
          Math.hypot(px, h0 - py),
          Math.hypot(w0 - px, h0 - py)
        ));
        const pivotAbsX = x + px;
        const pivotAbsY = y + py;
        x = Math.floor(pivotAbsX - radius);
        y = Math.floor(pivotAbsY - radius);
        width = radius * 2;
        height = radius * 2;
      }

      // 进度条成组时：包围盒取「进度条本体 + 进度条背景 + 指示器图片」三者尺寸的最大宽高，并以原矩形中心为基准扩展
      if (shape.type === ShapeType.ProgressBar) {
        const imageStore = useImagesStore();
        // 从 imagesMap 取尺寸（key 可能为 basename 或 path，需兼容查找）
        const getResourceMaxSize = (
          resourceName: 'progress' | 'bg' | 'indicatorImage'
        ): { width: number; height: number } => {
          let w = 0,
            h = 0;
          const res = shape.attrs.resources?.[resourceName];
          if (!res || typeof res !== 'object') return { width: w, height: h };
          Object.values(res).forEach((srcs) => {
            const arr = Array.isArray(srcs) ? srcs : [srcs];
            arr.forEach((src) => {
              if (!src) return;
              const item =
                imageStore.imagesMap[src] ??
                imageStore.imagesMap[
                  /[/\\]/.test(src) ? src.replace(/^.*[/\\]/, '') : src
                ];
              if (item?.width != null || item?.height != null) {
                w = Math.max(w, item.width ?? 0);
                h = Math.max(h, item.height ?? 0);
              }
            });
          });
          return { width: w, height: h };
        };
        const progressSize =
          shape.getImageSize(
            'progress',
            useEditorStore().themeColor?.id ?? ''
          ) ?? getResourceMaxSize('progress');
        const bgSize =
          shape.getImageSize('bg', useEditorStore().themeColor?.id ?? '') ??
          getResourceMaxSize('bg');
        const indicatorSize =
          shape.getImageSize(
            'indicatorImage',
            useEditorStore().themeColor?.id ?? ''
          ) ?? getResourceMaxSize('indicatorImage');
        const wProgress = progressSize?.width ?? width;
        const hProgress = progressSize?.height ?? height;
        const wBg = bgSize?.width ?? 0;
        const hBg = bgSize?.height ?? 0;
        const wInd = indicatorSize?.width ?? 0;
        const hInd = indicatorSize?.height ?? 0;
        const isLine = shape.attrs.progressBarType === 'line';
        // 直线进度条：总宽 = 进度条宽 + 指示器宽（并排），总高 = max(进度条, 指示器)，与 watch-renderer getSize 一致
        // 弧形进度条：总宽/高 = 三者最大，并以中心为基准扩展
        let newWidth: number;
        let newHeight: number;
        if (isLine) {
          // 组与进度条同位置、同尺寸 (160x21)，成组后子元素相对组为 (0,0)，不做任何偏移
          newWidth = wProgress + wInd;
          newHeight = Math.max(height, hProgress, hInd);
          width = Math.max(width, newWidth);
          height = newHeight;
        } else {
          newWidth = Math.max(width, wProgress, wBg, wInd);
          newHeight = Math.max(height, hProgress, hBg, hInd);
          const centerX = x + width / 2;
          const centerY = y + height / 2;
          x = centerX - newWidth / 2;
          y = centerY - newHeight / 2;
          width = newWidth;
          height = newHeight;
        }
      }

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + width);
      maxY = Math.max(maxY, y + height);
    }

    // 如果所有值都是 Infinity，说明没有有效的元素，返回默认值
    if (
      minX === Infinity ||
      minY === Infinity ||
      maxX === -Infinity ||
      maxY === -Infinity
    ) {
      return {
        x: 0,
        y: 0,
        w: 0,
        h: 0
      };
    }

    const result = {
      x: minX,
      y: minY,
      w: maxX - minX,
      h: maxY - minY
    };

    return result;
  }
  /**
   * 获取 layer 的宽高，用于成组等包围盒计算。
   * 元素优先用 KonvaUtils.getImageActualSize（绑定数据源后的实际渲染宽高），
   * 并与 attrs.w/h 取较大值，避免成组时 runtime 未就绪或 attrs 为模板尺寸导致组的 w/h 小于子元素。
   */
  static getLayerSize(layer: StoreLayerItem): { w: number; h: number } {
    const shape = Shape.getShape(layer.id);
    const attrsW = Number(layer.attrs?.w) || 0;
    const attrsH = Number(layer.attrs?.h) || 0;
    if (shape) {
      const { width, height } = KonvaUtils.getImageActualSize(shape);
      return {
        w: Math.max(width, attrsW),
        h: Math.max(height, attrsH)
      };
    }
    return { w: attrsW, h: attrsH };
  }

  // 根据宽高位置和旋转角度计算元素旋转后的实际位置和宽高
  static calcLayerWithRotation(
    rotation: number,
    origW: number,
    origH: number,
    actualX: number,
    actualY: number,
    align?: string
  ){
    let x = actualX;
    let y = actualY;
    actualX = align === 'center' ? actualX - origW / 2 : align === 'right' ? actualX - origW : actualX;
    // 矩形左上角 (actualX, actualY)，宽 width，高 height
    // 以 (drawX, drawY) 为中心点，顺时针旋转 rotation°
    const cos = Math.cos(((rotation ?? 0) * Math.PI) / 180);
    const sin = Math.sin(((rotation ?? 0) * Math.PI) / 180);
    // 旋转前四个角的绝对坐标：左上、右上、右下、左下
    const corners = [
      [actualX, actualY],
      [actualX + origW, actualY],
      [actualX + origW, actualY + origH],
      [actualX, actualY + origH]
    ];
    // 绕 (drawX, drawY) 顺时针旋转后的坐标
    const rotatedCorners = corners.map(([px, py]) => {
      const dx = px - x;
      const dy = py - y;
      return [
        round(x + dx * cos - dy * sin),
        round(y + dx * sin + dy * cos)
      ];
    });
    return rotatedCorners
  }
  /**
   * 仅基于 store 中 layer.attrs 计算包围盒（与子元素同一坐标系：相对共同父级）。
   * 用于成组时保证 layerRange 与子元素 attrs.x/y 一致，避免与 getClientRect 的绝对坐标混用导致相对位置错误。
   */
  static calcLayersRectFromAttrs(
    layers: StoreLayerItem[],
    isGroup = false
  ): LayersRect {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const layer of layers) {
      const { id } = layer;
      const shape = Shape.getShape(id);
      if (!shape) continue;
      let x = Number(layer.attrs?.x) || 0;
      let y = Number(layer.attrs?.y) || 0;
      const konvaService = KonvaService.getIns();
      let { width = 0, height = 0 } =
        konvaService?.getClientRect(id, isGroup) ?? {};
      let leftX = x;

      // 有旋转的元素，计算旋转后的 AABB 来扩展包围盒
      // 位置基于 attrs.x/y（与 setRelativeAttrs 坐标系一致），宽高扩展到覆盖旋转区域
      const rotation = Number(layer.attrs?.rotation) || 0;
      const hasRotation = rotation !== 0 && !shape.isPointerImage;
      if (hasRotation) {
        const runtimeStore = useRuntimeStore();
        const shapeState = runtimeStore.shapeState(id);
        const scale = layer.attrs?.scale ?? 1;
        let origW = Number(layer.attrs?.w) * scale;
        let origH = Number(layer.attrs?.h) * scale;
        if (shapeState?.width) {
          origW = shapeState?.width;
          origH = shapeState?.height;
        }
        
        // 旋转中心根据元素类型和 align 确定
        const align = layer.attrs?.align;
        const rotatedCorners = this.calcLayerWithRotation(rotation,round(origW),round(origH),x,y,align)
        // 取旋转后的 AABB 包围盒
        const xs = rotatedCorners.map((p) => p[0]);
        const ys = rotatedCorners.map((p) => p[1]);
        leftX = Math.min(...xs);
        y = Math.min(...ys);
        width = Math.max(...xs) - leftX;
        height = Math.max(...ys) - y;
      }
      // 无旋转时才做 ImageArray 的 align 和 unitIcon 修正，有旋转时 getClientRect 已包含这些
      if (!hasRotation && layer.type === ShapeType.ImageArray && width > 0) {
        const align = layer.attrs?.align;
        console.log(`[ALIGN-DEBUG] calcLayersRectFromAttrs | name=${shape.attrs.name} align=${align} attrs.x=${x} getClientRect.width=${width} leftX_before=${leftX}`);
        if (align === 'center') leftX = x - width / 2;
        else if (align === 'right') leftX = x - width;
        console.log(`[ALIGN-DEBUG] calcLayersRectFromAttrs | leftX_after=${leftX} range=[${leftX}, ${leftX + width}] rangeWidth=${width}`);
      }
      // widgetImageArray 的宽度已在 getClientRect 中精确计算，此处跳过避免双重修正
      if (
        !hasRotation &&
        shape.type === ShapeType.ImageArray &&
        shape.attrs.fillType === '数字填充' &&
        shape.attrs.resources?.unitIcon &&
        !shape.widgetImageArray
      ) {
        const imageStore = useImagesStore();
        const colorId =
          useEditorStore().themeColor?.id ??
          Object.keys(shape.attrs.resources?.unitIcon ?? {})[0] ??
          Object.keys(shape.attrs.resources?.images ?? {})[0];
        if (colorId) {
          let unitW = shape.getImageSize('unitIcon', colorId)?.width ?? 0;
          let unitH = shape.getImageSize('unitIcon', colorId)?.height ?? 0;
          if (!unitW && !unitH) {
            const res = shape.attrs.resources.unitIcon as Record<
              string,
              string[]
            >;
            Object.values(res || {}).forEach((srcs) => {
              const arr = Array.isArray(srcs) ? srcs : [srcs];
              arr.forEach((src) => {
                if (!src) return;
                const item =
                  imageStore.imagesMap[src] ??
                  imageStore.imagesMap[
                    /[/\\]/.test(src) ? src.replace(/^.*[/\\]/, '') : src
                  ];
                if (item?.width != null)
                  unitW = Math.max(unitW, item.width ?? 0);
                if (item?.height != null)
                  unitH = Math.max(unitH, item.height ?? 0);
              });
            });
          }
          if (unitW || unitH) {
            const {
              w: cw = 0,
              h: ch = 0,
              space = 0,
              decimalOffsetX = 0,
              scale: scaleAttr = 1
            } = shape.attrs;
            const scale =
              scaleAttr >= 10
                ? Number(scaleAttr) / 100
                : Number(scaleAttr) || 1;
            const sourceItems = shape.sourceItems ?? [];
            const includePrecision = sourceItems.some((i) => !!i?.precision);
            let digitCount = getImageArrayDigitCountBase(sourceItems);
            if (includePrecision) {
              let _p = 0;
              sourceItems.forEach((i) => {
                if (i?.precision && i.precision > _p) _p = i.precision;
              });
              digitCount += _p + 1;
            }
            const digitPart =
              digitCount * cw +
              (digitCount - 1) * space +
              Number(decimalOffsetX ?? 0);
            // 组宽=图组w+单位w（+间距），缩放后
            const groupW = (digitPart + space + unitW) * scale;
            const groupH = Math.max(ch, unitH) * scale;
            width = Math.max(width, Math.ceil(groupW));
            height = Math.max(height, Math.ceil(groupH));
          }
        }
      }
      // 如果是指针需要考虑指针旋转的全部区域
      // 指针图片（绑定了数据源的图片）：以 pivot 为圆心，pivot 到四角最远距离为半径，计算旋转扫过的正方形区域
      if (shape.isPointerImage) {
        const { attrs } = shape;
        const w0 = attrs.w ?? width;
        const h0 = attrs.h ?? height;
        const px = attrs.pivotX !== undefined ? Number(attrs.pivotX) : w0 / 2;
        const py = attrs.pivotY !== undefined ? Number(attrs.pivotY) : h0 / 2;
        // pivot 到四角的距离，取最大值作为旋转半径，向上取整避免浮点坐标导致成组后位置偏移
        const radius = Math.ceil(Math.max(
          Math.hypot(px, py),
          Math.hypot(w0 - px, py),
          Math.hypot(px, h0 - py),
          Math.hypot(w0 - px, h0 - py)
        ));
        // 旋转中心的绝对坐标
        const pivotAbsX = leftX + px;
        const pivotAbsY = y + py;
        leftX = Math.round(pivotAbsX - radius);
        y = Math.round(pivotAbsY - radius);
        width = radius * 2;
        height = radius * 2;
      }
      // 进度条成组时：包围盒取「进度条本体 + 进度条背景 + 指示器图片」三者尺寸的最大宽高，并以原矩形中心为基准扩展
      if (shape.type === ShapeType.ProgressBar) {
        const imageStore = useImagesStore();
        // 从 imagesMap 取尺寸（key 可能为 basename 或 path，需兼容查找）
        const getResourceMaxSize = (
          resourceName: 'progress' | 'bg' | 'indicatorImage'
        ): { width: number; height: number } => {
          let w = 0,
            h = 0;
          const res = shape.attrs.resources?.[resourceName];
          if (!res || typeof res !== 'object') return { width: w, height: h };
          Object.values(res).forEach((srcs) => {
            const arr = Array.isArray(srcs) ? srcs : [srcs];
            arr.forEach((src) => {
              if (!src) return;
              const item =
                imageStore.imagesMap[src] ??
                imageStore.imagesMap[
                  /[/\\]/.test(src) ? src.replace(/^.*[/\\]/, '') : src
                ];
              if (item?.width != null || item?.height != null) {
                w = Math.max(w, item.width ?? 0);
                h = Math.max(h, item.height ?? 0);
              }
            });
          });
          return { width: w, height: h };
        };
        const progressSize =
          shape.getImageSize(
            'progress',
            useEditorStore().themeColor?.id ?? ''
          ) ?? getResourceMaxSize('progress');
        const bgSize =
          shape.getImageSize('bg', useEditorStore().themeColor?.id ?? '') ??
          getResourceMaxSize('bg');
        const indicatorSize =
          shape.getImageSize(
            'indicatorImage',
            useEditorStore().themeColor?.id ?? ''
          ) ?? getResourceMaxSize('indicatorImage');
        const wProgress = progressSize?.width ?? width;
        const hProgress = progressSize?.height ?? height;
        const wBg = bgSize?.width ?? 0;
        const hBg = bgSize?.height ?? 0;
        const wInd = indicatorSize?.width ?? 0;
        const hInd = indicatorSize?.height ?? 0;
        const isLine = shape.attrs.progressBarType === 'line';
        // 直线进度条：总宽 = 进度条宽 + 指示器宽（并排），总高 = max(进度条, 指示器)，与 watch-renderer getSize 一致
        // 弧形进度条：总宽/高 = 三者最大，并以中心为基准扩展
        let newWidth: number;
        let newHeight: number;
        if (isLine) {
          // 组与进度条同位置、同尺寸 (160x21)，成组后子元素相对组为 (0,0)，不做任何偏移
          newWidth = wProgress + wInd;
          newHeight = Math.max(height, hProgress, hInd);
          width = Math.max(width, newWidth);
          height = newHeight;
        } else {
          newWidth = Math.max(width, wProgress, wBg, wInd);
          newHeight = Math.max(height, hProgress, hBg, hInd);
          const centerX = x + width / 2;
          const centerY = y + height / 2;
          x = centerX - newWidth / 2;
          y = centerY - newHeight / 2;
          width = newWidth;
          height = newHeight;
        }
      } 

      minX = Math.min(minX, leftX);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, leftX + width);
      maxY = Math.max(maxY, y + height);
    }
    if (
      minX === Infinity ||
      minY === Infinity ||
      maxX === -Infinity ||
      maxY === -Infinity
    ) {
      return { x: 0, y: 0, w: 0, h: 0 };
    }
    return {
      x: minX,
      y: minY,
      w: maxX - minX,
      h: maxY - minY
    };
  }

  static setRelativeAttrs(
    layer: StoreLayerItem,
    rect: Omit<LayersRect, 'w' | 'h'>
  ) {
    console.log(`[ALIGN-DEBUG] setRelativeAttrs | layer=${layer.attrs?.name} origX=${layer.attrs.x} origY=${layer.attrs.y} rect.x=${rect.x} rect.y=${rect.y} => newX=${layer.attrs.x - rect.x} newY=${layer.attrs.y - rect.y}`);
    layer.attrs.x = layer.attrs.x - rect.x;
    layer.attrs.y = layer.attrs.y - rect.y;
  }

  static setAbstractAttrs(
    layer: StoreLayerItem,
    rect: Omit<LayersRect, 'w' | 'h'>
  ) {
    layer.attrs.x = layer.attrs.x + rect.x;
    layer.attrs.y = layer.attrs.y + rect.y;
  }

  static getEmptyThemeStyle(): StoreThemeItem {
    return {
      id: generateID(),
      name: '样式1',
      type: 'normal',
      children: [],
      bgColor: '#000000',
      colorGroupTable: [
        {
          id: generateID(),
          name: '配色1',
          color: '#ffffff'
        }
      ]
    };
  }

  /**
   * 重置layer及其子layer的id
   */
  static regenerateId = (layerItem: StoreLayerItem) => {
    layerItem.attrs['_cloneFromId'] = layerItem.id;
    layerItem.id = generateID();
    layerItem.children?.forEach((layerItem) => this.regenerateId(layerItem));
  };

  /**
   * 重置资源的colorid
   */
  static resetResourceColor = (
    layerItem: StoreLayerItem,
    newColorIds: string[]
  ) => {
    const resourceNames = Object.keys(layerItem.attrs.resources ?? {});
    // layerItem.attrs.resources = {};
    const resources: {
      [name: string]: LayerResourceItem;
    } = {};

    resourceNames.forEach((resourceName) => {
      // 获取原来的配色和图片数据
      const imageItems = Object.values(
        layerItem.attrs.resources?.[resourceName] ?? {}
      );

      const res: LayerResourceItem = {};
      newColorIds.forEach((themeColorId, index) => {
        res[themeColorId] = imageItems[index] ?? [];
      });
      resources[resourceName] = res;
    });
    layerItem.attrs.resources = resources;

    layerItem.children?.forEach((layerItem) =>
      this.resetResourceColor(layerItem, newColorIds)
    );
  };

  /**
   * 重置主题的themeColor以及资源的color id
   */
  static resetThemeColorId = (theme: StoreThemeItem, resetColorId = true) => {
    if (resetColorId) {
      theme.colorGroupTable.forEach((item) => (item.id = generateID()));
    }
    const themeColorIds = theme.colorGroupTable.map((item) => item.id);
    theme.children.forEach((layer) => {
      this.resetResourceColor(layer, themeColorIds);
    });
  };

  static copyColorResource = (
    layers: StoreLayerItem[],
    fromId: string,
    toId: string
  ) => {
    layers.forEach((layer) => {
      const shape = Shape.getShape(layer.id);
      const layerAttrs = shape?.layer?.attrs;
      if (!layerAttrs) return;
      if (!layerAttrs.resources) layerAttrs.resources = {};
      const resources = layerAttrs.resources;
      Object.keys(resources).forEach((resourceName) => {
        const item2 = resources[resourceName as ResourceKey];
        if (item2) {
          item2[toId] = cloneDeep(item2[fromId]);
          if (shape.attrs.resources)
            shape.attrs.resources[resourceName as ResourceKey] = item2;
        }
      });

      // 复制视频按样式隔离的数据 videoColorData
      if (layerAttrs.videoColorData?.[fromId]) {
        if (!layerAttrs.videoColorData) layerAttrs.videoColorData = {};
        layerAttrs.videoColorData[toId] = cloneDeep(
          layerAttrs.videoColorData[fromId]
        );
      }
      // 复制 spriteExtra 中每项的 videoColorData（原地修改，避免断开引用）
      if (layerAttrs.spriteExtra?.length) {
        layerAttrs.spriteExtra.forEach(
          (item: Record<string, unknown>) => {
            const vcd = item.videoColorData as
              | Record<string, unknown>
              | undefined;
            if (vcd?.[fromId]) {
              vcd[toId] = cloneDeep(vcd[fromId]);
            }
          }
        );
      }

      if (layer.children?.length)
        this.copyColorResource(layer.children, fromId, toId);
    });
  };

  private static _deleteResorceIfColorNotExists(
    colorIds: string[],
    layer: StoreLayerItem
  ) {
    const resources = layer?.attrs.resources;
    const resourceNames = Object.keys(resources ?? {});
    if (!resourceNames.length || !resources) return;
    resourceNames.forEach((resourceName) => {
      const _ids = Object.keys(resources[resourceName] ?? {});
      _ids.forEach((_id) => {
        if (!colorIds.includes(_id)) {
          delete resources[resourceName][_id];
        }
      });
    });
  }
  static deleteResorceIfColorNotExists(theme: StoreThemeItem) {
    const layers: StoreLayerItem[] = [...theme.children];
    const ids = theme.colorGroupTable.map((item) => item.id);
    while (layers.length) {
      const layer = layers.shift();
      if (layer?.children?.length) layers.push(...(layer.children ?? []));
      if (!layer) continue;
      this._deleteResorceIfColorNotExists(ids, layer);
    }
  }

  static preHandleLayers(
    theme: StoreThemeItem,
    deviceType: WatchfaceDeviceType,
    desc: Description
  ) {
    const webVersionCreatedAt = desc.webVersionCreatedAt ?? '1970-01-01 00:00';
    const layers: StoreLayerItem[] = [...theme.children];
    const ids = theme.colorGroupTable.map((item) => item.id);
    // 将原来的文本text修改为strings
    const setTextStrings = (layer: StoreLayerItem) => {
      if (layer.type !== ShapeType.Text || layer.attrs?.strings?.length) return;
      const { text, color, source, sourceFormat } = layer.attrs ?? {};
      layer.attrs.strings = [
        {
          value: source ? source : text,
          color,
          isSource: !!source,
          sourceFormat
        }
      ];
    };

    const checkFontId = (
      layer: StoreLayerItem,
      deviceType: WatchfaceDeviceType
    ) => {
      if (layer.type !== ShapeType.Text) return;
      const fonts = useTextFont().getFontIdList(deviceType);
      const fontId = layer.attrs.fontId;
      const fontWeight = layer.attrs.fontWeight;
      const font = fonts.find((item) => item.value == fontId);
      if (!font) {
        console.log('font id not found', fonts, deviceType);

        layer.attrs.fontId = '';
        layer.attrs.fontWeight = '';
      } else {
        if (!font.weights.find((item) => item.value == fontWeight)) {
          layer.attrs.fontWeight = '';
        }
      }
    };

    const changeGroupToWidget = (layer: StoreLayerItem) => {
      if (layer.type == ShapeType.Group) {
        layer.type = ShapeType.Widget;
      }
    };
    // healthCalorie数据源已废弃，用healthCalorieValue代替
    const correctDataSource = (layer: StoreLayerItem) => {
      // 更正拼写错误
      const dataSourceKeyMap = {
        AppAlarmMinute: 'appAlarmMinute',
        AppAlarmHour: 'appAlarmHour',
        healthCalorie: 'healthCalorieValue',
        healthHearRateZone: 'healthHeartRateZone'
      };
      Object.entries(dataSourceKeyMap).forEach((dataSourceKey) => {
        const [oldKey, newKey] = dataSourceKey;
        if (layer.attrs.source == oldKey) layer.attrs.source = newKey;
        layer.attrs?.strings?.forEach(
          (item: { isSource: boolean; value: string }) => {
            if (item.isSource && item.value == oldKey) {
              item.value = newKey;
            }
          }
        );
      });
      // 处理时间timeHour12数据源
      if (
        layer.type == ShapeType.ImageArray &&
        layer.attrs.fillType == '普通填充'
      ) {
        if (layer.attrs.source == 'timeHour12H') {
          layer.attrs.source = 'timeHour';
          layer.attrs.sourceHourMode = '12h';
        }
        if (layer.attrs.source == 'timeHour24H') {
          layer.attrs.source = 'timeHour';
          layer.attrs.sourceHourMode = '24h';
        }
      }
    };

    const addDefaultSourceFormat = (layer: StoreLayerItem,type: string) => {
      const { sourceFormat, source } = layer.attrs;
      if (!source) return;
      const { findOne } = useDataSource();
      const sourceItem = findOne(source,type);
      if (!sourceItem) return;
      const formats = sourceItem.formats;
      if (!formats?.length) {
        if (sourceFormat) layer.attrs.sourceFormat = undefined;
      } else {
        if (!formats.includes(sourceFormat)) {
          layer.attrs.sourceFormat = undefined;
          const withUnit =
            Object.keys(layer.attrs?.resources?.['unitIcon'] ?? {}).length > 0;
          formats.forEach((format) => {
            if (layer.attrs.sourceFormat) return;
            switch (format) {
              case DataSourceFormat.Format_8:
              case DataSourceFormat['Format_8.0']:
              case DataSourceFormat.Format_08:
                // 不需要单位的
                if (!withUnit) {
                  layer.attrs.sourceFormat = format;
                }
                break;
              case DataSourceFormat.Format_8时:
              case DataSourceFormat['Format_8.0时']:
              case DataSourceFormat.Format_08时:
              case DataSourceFormat.Format_SPF8:
              case DataSourceFormat['Format_SPF8.0']:
                // 需要单位的
                if (withUnit) {
                  layer.attrs.sourceFormat = format;
                }
                break;
            }
          });
          layer.attrs.sourceFormat = layer.attrs.sourceFormat ?? formats[0];
        }
      }
    };

    const handleTempature = (layer: StoreLayerItem) => {
      const { sourceFormat, source } = layer.attrs;
      if (!source) return;
      const sourceItem = dataSourceWholeItems.find(
        (item) => item.key == source
      );
      if (!sourceItem) return;
      const formats = sourceItem.formats;
      if (disabledDataSourceFormat(sourceItem.key, sourceFormat)) {
        // 如果默认样式是被禁用的，将样式改为第一个非禁用的样式
        layer.attrs.sourceFormat = formats
          ?.filter((item) => item !== sourceFormat)
          .shift();
      }
    };
    const handleAMPM = (layer: StoreLayerItem) => {
      if (layer.type != ShapeType.ImageArray) return;
      if (layer.attrs.source != 'miscIsAM') return;
      layer.attrs.source = 'miscIsPM';
      const images = layer.attrs?.resources?.['images'] ?? {};
      const colorIds = Object.keys(images);
      if (!colorIds.length) return;
      colorIds.forEach((colorId) => {
        const srcs = images[colorId];

        [srcs[1], srcs[0]] = [srcs[0] ?? '', srcs[1] ?? ''];
        images[colorId] = srcs;
      });
    };

    const renamejsFilename = (layer: StoreLayerItem) => {
      if (layer.type != ShapeType.JsFile) return;
      if (layer.attrs['fileName'] && !layer.attrs['jsFilename']) {
        layer.attrs['jsFilename'] = layer.attrs['fileName'];
        delete layer.attrs['fileName'];
      }
    };
    // const disableAutoSize = (layer: StoreLayerItem) => {
    //   if (layer.type != ShapeType.Text) return;
    //   if (layer.attrs.sizeContent) layer.attrs.sizeContent = false;
    // };
    const resetSecondPointer = (layer: StoreLayerItem) => {
      if (layer.type == ShapeType.Image) {
        if (layer.attrs.source != 'timeSecond') return;
        if (layer.attrs.pointerFps) return;
        const parameter = Number(layer.attrs.parameter);
        if (parameter <= 17) {
          layer.attrs.pointerFps = 60;
        } else if (parameter <= 40) {
          layer.attrs.pointerFps = 30;
        } else {
          layer.attrs.pointerFps = 1;
        }
        delete layer.attrs['parameter'];
      }
      if (layer.type == ShapeType.Sprite) {
        if (layer.attrs.interval && !layer.attrs.spriteFps)
          layer.attrs.spriteFps = Math.floor(
            1000 / Number(layer.attrs.interval)
          );
        delete layer.attrs['interval'];
      }
    };

    const resetWidgetFlex = (layer: StoreLayerItem) => {
      if (layer.type != ShapeType.Widget) return;
      if (!layer.attrs['alignItems'] && layer.attrs['alignContent']) {
        layer.attrs['alignItems'] = layer.attrs['alignContent'];
        delete layer.attrs['alignContent'];
      }
    };

    const correctXYWH = (layer: StoreLayerItem) => {
      ['x', 'y', 'w', 'h'].forEach((key) => {
        const val = layer.attrs[key];
        if (val == undefined || val == null) {
          layer.attrs[key] = 0;
        }
        if (typeof val == 'string') layer.attrs[key] = Number(val);
        // 转为整数
        layer.attrs[key] = Math.round(Number(val));
      });
    };

    const reverseSlotChildren = (
      layer: StoreLayerItem,
      webVersionCreatedAt: string
    ) => {
      if (layer.type !== ShapeType.Slot) return;

      if (
        // 2024-01-18 00:00之前的版本需要重置
        dayjs(webVersionCreatedAt).isBefore('2024-01-18 00:00') &&
        !layer.attrs?.reversed
      ) {
        try {
          layer.children?.reverse();
        } finally {
          layer.attrs.reversed = true;
        }
      }
    };
    const reverseTextArcVerticalAlign = (
      layer: StoreLayerItem,
      webVersionCreatedAt: string
    ) => {
      if (layer.type !== ShapeType.Text) return;

      if (
        // 2024-3-12 18:00之前的版本需要反转垂直方向
        dayjs(webVersionCreatedAt).isBefore('2024-3-12 18:00') &&
        layer.attrs.verticalAlign &&
        layer.attrs.verticalAlign != 'center' &&
        !layer.attrs?.verticalAlignReversed
      ) {
        try {
          const _align = layer.attrs.verticalAlign;
          layer.attrs.verticalAlign =
            _align == 'under' ? 'top' : _align == 'top' ? 'under' : 'center';
        } finally {
          layer.attrs.verticalAlignReversed = true;
        }
      }
    };
    const addSlotDisplayWidget = (layer: StoreLayerItem) => {
      if (layer.type !== ShapeType.Slot) return;
      if (!layer.children?.length) return;
      // 'none' 表示可选无状态，不需要重置
      if (layer.attrs.includeEmpty && layer.attrs.widgetIdToDisplay === 'none') return;
      if (
        layer.children?.some((item) => item.id == layer.attrs.widgetIdToDisplay)
      )
        return;

      layer.attrs.widgetIdToDisplay =
        layer.children[layer.children?.length - 1]?.id;
    };

    const copySlotEditbox = (layer: StoreLayerItem) => {
      if (layer.type !== ShapeType.Slot) return;
      if (!layer.children?.length) return;
      const editbox = layer.attrs.resources?.['editBox'];
      if (!editbox) return;
      layer.children.forEach((widget) => {
        if (!widget.attrs.resources?.['editBox']) {
          if (!widget.attrs.resources) widget.attrs.resources = {};
          widget.attrs.resources['editBox'] = cloneDeep(editbox);
        }
      });
    };

    // 删除字符串类型数据，因为不删除可能会引起重启问题，
    // 其他类型的暂时不考虑了，
    const deleteUnsupportDataSourceString = (
      layer: StoreLayerItem,
      deviceType: WatchfaceDeviceType
    ) => {
      if (layer.type == ShapeType.Text) {
        const strings = (layer.attrs.strings ?? []) as TextString[];
        if (
          strings.some(
            (item) => item.isSource && !canUseDataSource(item.value, deviceType)
          )
        ) {
          layer.attrs.strings = [];
        }
      }
    };
    const correctIndicatorRadius = (layer: StoreLayerItem) => {
      if (layer.type == ShapeType.ProgressBar) {
        const number = layer.attrs['indicatorRadius'] ?? 0;
        layer.attrs['indicatorRadius'] = Math.abs(number);
      }
    };

    const setRecolorDisEnableIfNeed = (layer: StoreLayerItem) => {
      if (layer.attrs['recolorEnable'] == true) {
        layer.attrs['recolorEnable'] = false;
      }
    };

    const addBarRadiusAndBarWidth = (layer: StoreLayerItem) => {
      if (layer.type == ShapeType.ProgressBar) {
        const {
          innerRingPercent,
          w = 0,
          barWidth,
          barRadius
        } = layer.attrs ?? {};
        if (innerRingPercent == undefined) return;
        if (barWidth != undefined || barRadius != undefined) return;

        const r = w / 2;
        const innerRingRadius = Math.floor((r * innerRingPercent) / 100);
        const _barWidth = r - innerRingRadius;
        const _barRadius = innerRingRadius + _barWidth / 2;
        layer.attrs.barWidth = _barWidth;
        layer.attrs.barRadius = _barRadius;
      }
    };

    const resetPointerRegionNum = (layer: StoreLayerItem) => {
      if (canUse('pointer-light-source').valid) return;
      if (layer.type == ShapeType.Image && layer.attrs.pointerRegionNum) {
        layer.attrs.pointerRegionNum = 1;
      }
    };

    const resetPhotoPosition = (layer: StoreLayerItem) => {
      if (layer.type == ShapeType.PhotoAlbumBg) {
        layer.attrs.x = 0;
        layer.attrs.y = 0;
      }
    };

    const handleTempatureUnitType = (
      layer: StoreLayerItem,
      deviceType: WatchfaceDeviceType,
      descTemperatureUnitType?: string
    ) => {
      const sourceKey = layer.attrs['source'];
      const attrKey = 'temperatureUnitType';
      // 只需要在支持的元素上添加
      if (!supportTemperatureType(layer.type as ShapeType, sourceKey)) return;

      // 如果已有值，则重新赋值即可
      const _canuse = supportFahrenheit(deviceType, sourceKey);
      if (!layer.attrs[attrKey]) {
        // 根据是否支持添加默认值
        layer.attrs[attrKey] = descTemperatureUnitType
          ? descTemperatureUnitType
          : _canuse
          ? 'system'
          : 'celsius';
      }

      // 最后进行校验
      if (['system', 'fahrenheit'].includes(layer.attrs[attrKey]) && !_canuse) {
        layer.attrs[attrKey] = 'celsius';
      }
    };

    const deleteUnsupportRotaion = (
      layer: StoreLayerItem,
      deviceType: WatchfaceDeviceType
    ) => {
      if (layer.type == ShapeType.ImageArray) {
        if (!canUse('image-array-rotation', deviceType).valid) {
          layer.attrs['rotation'] = 0;
        }
      }
    };

    // 是否支持多序列帧和多视频
    const delUnsupportMultiSprites = (
      layer: StoreLayerItem,
      deviceType: WatchfaceDeviceType
    ) => {
      if (!canUse('multi-sprite-video', deviceType).valid) {
        layer.attrs['spriteExtra'] = [];
      }
    };

    while (layers.length) {
      const layer = layers.shift();
      if (layer?.children?.length) layers.push(...(layer.children ?? []));
      if (!layer) continue;
      this._deleteResorceIfColorNotExists(ids, layer);
      setTextStrings(layer);
      changeGroupToWidget(layer);
      correctDataSource(layer);
      addDefaultSourceFormat(layer,theme.type);

      // 处理am pm数据源
      handleAMPM(layer);

      // 处理温度不支持禁用
      handleTempature(layer);

      renamejsFilename(layer);

      // 放开文本自适应
      // disableAutoSize(layer);

      resetSecondPointer(layer);

      // 修复widget flex布局交叉轴元素设置错误问题
      resetWidgetFlex(layer);

      // 更正xywh
      correctXYWH(layer);

      // revese children
      reverseSlotChildren(layer, webVersionCreatedAt);
      // 添加容器默认显示的内容
      addSlotDisplayWidget(layer);
      copySlotEditbox(layer);
      reverseTextArcVerticalAlign(layer, webVersionCreatedAt);
      // 去除不支持的数据源-仅考虑文本类型
      deleteUnsupportDataSourceString(layer, deviceType);
      // 文本字重需要和设备匹配
      checkFontId(layer, deviceType);

      // 处理recolor问题
      if (!canUse('photoAlbumBg').valid) setRecolorDisEnableIfNeed(layer);

      resetResources(layer);

      // 处理indicatorRadius为负值问题
      correctIndicatorRadius(layer);

      // 添加进度条半径和宽度属性
      addBarRadiusAndBarWidth(layer);

      // 处理pointerRegionNum
      resetPointerRegionNum(layer);

      // 修正相册图层XY坐标为0
      resetPhotoPosition(layer);

      // 处理温度单位类型
      // 注意，以下两个函数式互斥的
      handleTempatureUnitType(
        layer,
        deviceType,
        desc['temperatureUnitType' as '_id']
      );

      // 删除图组旋转属性
      deleteUnsupportRotaion(layer, deviceType);

      // 删除多序列帧和多视频
      delUnsupportMultiSprites(layer, deviceType);

      // 清除子元素不足2个的 Widget 上残留的条件切换动效数据
      if (
        layer.type === ShapeType.Widget &&
        (layer.children?.length ?? 0) <= 1 &&
        layer.attrs.widgetConditions?.length
      ) {
        layer.attrs.widgetConditions = undefined;
        layer.attrs.widgetAnimations = undefined;
      }
    }
  }

  static preHandleThemes(themes: StoreThemeItem[]) {
    const correctMultiAODThemes = (themes: StoreThemeItem[]) => {
      const firstAOD = themes.find((theme) => theme.type == 'AOD');
      if (firstAOD) {
        themes = themes.filter(
          (theme) => theme.type == 'normal' || theme.id == firstAOD.id
        );
        firstAOD.isSingleAOD = true;
      }
      return themes;
    };
    if (canUse('multi-AOD-themes').valid != true) {
      themes = correctMultiAODThemes(themes);
    }

    // 判断息屏和亮屏是否对应
    const normalThemes = themes.filter((item) => item.type == 'normal');
    const AODThemes = themes.filter((item) => item.type == 'AOD');
    let aodIndex = 0;
    AODThemes.forEach((AOD) => {
      // 查找对应的亮屏
      const normal = normalThemes.find((item) => AOD.name == item.name);
      if (normal) return;
      // 代表息屏对应错误，需要重新对应
      const normal2 = normalThemes[aodIndex];
      if (!normal2) return;
      AOD.name = normal2.name + '的息屏';
      aodIndex++;
    });

    // 判断是否支持多配色
    themes.forEach((theme) => {
      // 在不支持多配色时，删除多余配色
      if (
        !canUse('multi-color-group').valid &&
        theme.colorGroupTable.length > 1
      ) {
        theme.colorGroupTable = [theme.colorGroupTable[0]];
      }
    });

    return themes;
  }

  static correctThemeLayersByWatchfaceType = (
    themes: StoreThemeItem[],
    watchfaceType: Description['watchfaceType']
  ) => {
    const unSupportShapes = getUnSupportShapesByWatchfaceType(watchfaceType);
    themes.forEach((theme) => {
      theme.children = theme.children.filter((item) => {
        return unSupportShapes.includes(item.type as ShapeType) ? false : true;
      });
    });

    return themes;
  };
}
